import { cn } from '@dreamstack/util/src/cn'
import React from 'react'

// Helper type: intrinsic div props without the ref (we re-declare ref via forwardRef)
type DivProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'ref'> & {
  className?: string
}

// Base grid with gaps (no columns defined yet)
export const GridWithGapsNoCols = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<DivProps>
>(({ className, children, ...rest }, ref) => (
  <div
    ref={ref}
    className={cn(
      'grid gap-x-[16px] md:gap-x-[48px] lg:gap-x-[64px]',
      className
    )}
    {...rest}
  >
    {children}
  </div>
))
GridWithGapsNoCols.displayName = 'GridWithGapsNoCols'

// Adds 12 columns
export const GridWithGaps = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<DivProps>
>(({ className, children, ...rest }, ref) => (
  <GridWithGapsNoCols
    ref={ref}
    className={cn('grid-cols-12', className)}
    {...rest}
  >
    {children}
  </GridWithGapsNoCols>
))
GridWithGaps.displayName = 'GridWithGaps'

// Adds horizontal padding responsive
export const GridWithGapsAndPadding = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<DivProps>
>(({ className, children, ...rest }, ref) => (
  <GridWithGaps
    ref={ref}
    className={cn(
      'px-[24px] md:px-[48px] lg:pl-[218px] lg:pr-[218px]',
      className
    )}
    {...rest}
  >
    {children}
  </GridWithGaps>
))
GridWithGapsAndPadding.displayName = 'GridWithGapsAndPadding'

// Full width helper respecting original calculations
// If Grid Padding changes, change this one as well to maintain full width blocks
// Explanation for 'ml' and 'lg' formula: Since the main-grid layout has a max-w-screen width, taking off the padding alone is not enough, we also have to subtract the
// auto generated margin, therefore we take 100vw and subtract the max-w-screen width for the viewport (ml: 1366px, lg: 1920px) and also subtract the padding times 2
// (ml: 2*48px = 96px, lg: 2*218px = 436px)
export const FullGridWidthDiv = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<DivProps>
>(({ className, children, ...rest }, ref) => (
  <div
    ref={ref}
    className={cn(
      '-mx-px-24 w-[calc(100%+2*24px)] md:-mx-px-48 md:w-[calc(100%+2*48px)] ml:ml-[calc(-1*((100vw-1366px+96px)/2))] ml:mr-[calc(-1*((100vw-1366px+96px)/2))] ml:w-[100vw] lg:ml-[calc(-1*((100vw-1920px+436px)/2))] lg:mr-[calc(-1*((100vw-1920px+436px)/2))] lg:w-[100vw]',
      className
    )}
    {...rest}
  >
    {children}
  </div>
))
FullGridWidthDiv.displayName = 'FullGridWidthDiv'

export const MainGridStyled = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<DivProps>
>(({ className, children, ...rest }, ref) => (
  <GridWithGapsAndPadding
    ref={ref}
    className={cn(
      'w-full max-w-screen-ml lg:max-w-screen-lg mx-auto',
      className
    )}
    {...rest}
  >
    {children}
  </GridWithGapsAndPadding>
))
MainGridStyled.displayName = 'MainGridStyled'

export const MainContainer = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<DivProps>
>(({ children, className, ...rest }, ref) => (
  <MainGridStyled ref={ref} className={className} {...rest}>
    <div className="col-span-12">{children}</div>
  </MainGridStyled>
))
MainContainer.displayName = 'MainContainer'

export const MainGrid = MainGridStyled
